/* eslint-env browser */
// Reports › Driver earnings (wave 3, W2_Portal_Tables look): one row per
// driver and month in the modern table (money right-aligned in tabular
// figures, month names in the portal's language, Export with CSV and print
// that keep Amharic names).
//
// Data, exactly as before: the earnings feed (fetchDriverEarnings) is asked
// for once per mount, only when the slice is empty and not already loading
// (deferred from AuthLoading for RTDB cost), so a tab switch never re-runs
// the heavy read; Retry is the same single read, asked for by the person.
import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { api } from 'common';
import Box from '@mui/material/Box';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { useTranslation } from 'react-i18next';
import ClickableUserLink from '../components/ClickableUserLink';
import { DataTable } from '../components/modern';

// The earnings feed reports an empty result as a failure with this text.
const NO_DATA_MSG = 'No data available.';

const NUMBER_FORMAT = new Intl.NumberFormat('en-US');
const fmt = (n) => NUMBER_FORMAT.format(n || 0);

// Month names in the portal's language (Gregorian months, as the data is),
// falling back to the English name the feed already carries.
const monthName = (lang, monthIndex, fallback) => {
  const m = Number(monthIndex);
  if (!Number.isInteger(m) || m < 0 || m > 11) return fallback || '';
  try {
    return new Intl.DateTimeFormat(lang || 'en', { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2000, m, 15)));
  } catch (e) {
    return fallback || '';
  }
};

export default function DriverEarning() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const columns = [
    { id: 'year', title: t('year'), field: 'year' },
    {
      id: 'month',
      title: t('months'),
      field: 'monthsName',
      exportValue: (rowData) => monthName(lang, rowData.month, rowData.monthsName),
      searchValue: (rowData) => monthName(lang, rowData.month, rowData.monthsName),
      customSort: (a, b) => Number(a.year) - Number(b.year) || Number(a.month) - Number(b.month),
      render: (rowData) => monthName(lang, rowData.month, rowData.monthsName),
    },
    {
      id: 'driverName',
      title: t('driver_name'),
      field: 'driverName',
      render: (rowData) =>
        rowData.driverName ? (
          <ClickableUserLink value={rowData.driverName} type="driver" searchField="name" userId={rowData.driverId || rowData.driverUId} iconType="car" />
        ) : (
          '-'
        ),
    },
    { id: 'total_rides', title: t('booking_count'), field: 'total_rides', type: 'number' },
    { id: 'driverVehicleNo', title: t('vehicle_reg_no'), field: 'driverVehicleNo' },
    { id: 'driverShare', title: t('earning_amount'), field: 'driverShare', type: 'money' },
  ];

  const [data, setData] = useState([]);
  const driverearningdata = useSelector((state) => state.driverearningdata);
  const dispatch = useDispatch();
  const { fetchDriverEarnings } = api;
  // Deferred from AuthLoading (RTDB cost): fetch once per mount, and only when
  // the slice is empty and not already loading, so tab-switch remounts never
  // re-run the heavy legacy read.
  const requestedRef = useRef(false);
  useEffect(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;
    if (!driverearningdata.driverearnings && !driverearningdata.loading && !(driverearningdata.error && driverearningdata.error.flag)) {
      dispatch(fetchDriverEarnings());
    }
  }, [dispatch, fetchDriverEarnings, driverearningdata.driverearnings, driverearningdata.loading, driverearningdata.error]);

  useEffect(() => {
    if (driverearningdata.driverearnings) {
      setData(driverearningdata.driverearnings);
    }
  }, [driverearningdata.driverearnings]);

  // Loading, failed and empty states: the table's header shows at once, the
  // body says what is happening. Retry is the same single read, asked for by
  // the person (never automatic).
  const loading = !!driverearningdata.loading;
  const error = driverearningdata.error || {};
  const failed = !loading && !!error.flag && error.msg !== NO_DATA_MSG;
  const retry = () => dispatch(fetchDriverEarnings());

  const title = t('driver_earning_title');

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <DataTable
        title={title}
        rows={data}
        columns={columns}
        rowKey={(row, index) => `${row.driverId || row.driverUId || row.driverName || 'd'}-${row.year}-${row.month}-${index}`}
        loading={loading && !data.length}
        refreshing={loading && data.length > 0}
        error={
          failed
            ? {
                title: t('earnings_load_failed', { defaultValue: 'Could not load driver earnings.' }),
                onRetry: retry,
              }
            : null
        }
        countText={({ shown, total, query, loading: busy }) =>
          busy
            ? t('earnings_loading', { defaultValue: 'Loading driver earnings…' })
            : query
              ? t('table_count_shown', { shown: fmt(shown), total: fmt(total), defaultValue: '{{shown}} of {{total}}' })
              : t('table_count', { count: total, n: fmt(total), defaultValue_one: '{{n}} row', defaultValue_other: '{{n}} rows', defaultValue: '{{n}} rows' })
        }
        prefsKey="driver_earnings"
        exportCsv
        exportPrint
        exportFileName="driver_earnings"
        exportTitle={title}
        minWidth={820}
        emptyState={({ query }) =>
          query || failed
            ? null
            : {
                icon: <PaymentsOutlinedIcon />,
                title: t('earnings_empty', { defaultValue: 'No driver earnings yet' }),
                body: t('earnings_empty_body', { defaultValue: 'Each month shows here once drivers have completed and paid trips.' }),
              }
        }
      />
    </Box>
  );
}
